import React, { useState, useEffect, useRef } from 'react';
import styled from 'styled-components';
import { useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faUser,
  faBars,
  faSignOutAlt,
} from '@fortawesome/free-solid-svg-icons';
import Footer from '../components/Footer';
import { getSubscribedLocations } from '../services/api';
import { isPaidSubscriptionStatus } from '../utils/subscriptionStatus';
import { getStoredSubscriptionDisplayPrice, isTestSubscription } from '../utils/subscriptionPlans';
import { useLogoutHandler } from '../utils/logoutHandler';
import LogoutConfirmDialog from '../components/LogoutConfirmDialog';
import Sidebar from '../components/Sidebar/Sidebar';
import theme from '../styles/theme';

const SIDEBAR_WIDTH = 260;
const SIDEBAR_COLLAPSED = 72;

const DashboardContainer = styled.div`
  display: flex;
  min-height: 100vh;
  background: ${theme.colors.bgMesh};

  @media (max-width: 768px) {
    flex-direction: column;
  }
`;

const MainContent = styled.div<{ isCollapsed: boolean }>`
  flex: 1;
  margin-left: ${props => (props.isCollapsed ? SIDEBAR_COLLAPSED : SIDEBAR_WIDTH)}px;
  padding: 0;
  padding-bottom: 60px;
  padding-top: 80px;
  transition: margin-left 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  width: calc(100% - ${props => (props.isCollapsed ? SIDEBAR_COLLAPSED : SIDEBAR_WIDTH)}px);
  min-width: 0;

  @media (max-width: 768px) {
    margin-left: 0;
    width: 100%;
    padding-top: 72px;
  }
`;

const TopBar = styled.div<{ isCollapsed: boolean }>`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 14px 28px;
  background: rgba(255, 255, 255, 0.9);
  backdrop-filter: blur(16px);
  -webkit-backdrop-filter: blur(16px);
  border-bottom: 1px solid ${theme.colors.cardBorder};
  box-shadow: ${theme.shadows.topbar};
  position: fixed;
  top: 0;
  right: 0;
  left: ${props => (props.isCollapsed ? SIDEBAR_COLLAPSED : SIDEBAR_WIDTH)}px;
  z-index: 100;
  height: 64px;
  transition: left 0.3s cubic-bezier(0.4, 0, 0.2, 1);

  @media (max-width: 768px) {
    left: 0;
    width: 100%;
    padding: 14px 20px;
  }
`;

const ToggleButton = styled.button`
  background: ${theme.colors.card};
  border: 1px solid ${theme.colors.cardBorder};
  color: ${theme.colors.textSecondary};
  font-size: 18px;
  cursor: pointer;
  padding: 10px 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: ${theme.radius.md};
  transition: all 0.2s ease;
  box-shadow: ${theme.shadows.sm};
  flex-shrink: 0;

  &:hover {
    background: rgba(255, 107, 107, 0.08);
    border-color: rgba(255, 107, 107, 0.3);
    color: ${theme.colors.primary};
  }
`;

const UserProfile = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  position: relative;
  cursor: pointer;
  padding: 6px 10px 6px 16px;
  border-radius: ${theme.radius.pill};
  border: 1px solid ${theme.colors.cardBorder};
  background: ${theme.colors.card};
  transition: all 0.2s ease;
  box-shadow: ${theme.shadows.sm};
  flex-shrink: 0;

  &:hover {
    border-color: rgba(255, 107, 107, 0.35);
    box-shadow: ${theme.shadows.md};
  }
`;

const UserInfo = styled.div`
  display: flex;
  flex-direction: column;
`;

const UserName = styled.div`
  font-size: 14px;
  font-weight: 600;
  color: ${theme.colors.text};
  line-height: 1.2;
  letter-spacing: -0.02em;
`;

const UserAvatar = styled.div`
  width: 38px;
  height: 38px;
  border-radius: 50%;
  background: ${theme.colors.accentGradient};
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 16px;
  font-weight: 700;
  box-shadow: 0 2px 8px rgba(255, 107, 107, 0.35);
`;

const UserDropdown = styled.div<{ isOpen: boolean }>`
  position: absolute;
  top: calc(100% + 10px);
  right: 0;
  background: ${theme.colors.card};
  border-radius: ${theme.radius.lg};
  border: 1px solid ${theme.colors.cardBorder};
  box-shadow: ${theme.shadows.lg};
  min-width: 200px;
  display: ${props => (props.isOpen ? 'block' : 'none')};
  z-index: 1000;
  overflow: hidden;
`;

const DropdownItem = styled.button`
  display: flex;
  align-items: center;
  width: 100%;
  padding: 12px 16px;
  border: none;
  background: none;
  color: ${theme.colors.textSecondary};
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  text-align: left;
  transition: all 0.15s ease;

  &:hover {
    background: rgba(255, 107, 107, 0.06);
    color: ${theme.colors.primary};
  }

  svg {
    width: 16px;
    margin-right: 12px;
  }
`;

const PageContent = styled.div`
  padding: 8px 24px 32px;
  max-width: 1280px;
  margin: 0 auto;
  width: 100%;
  box-sizing: border-box;

  @media (max-width: 768px) {
    padding: 8px 16px 24px;
  }
`;

const PageHeader = styled.div`
  margin-bottom: 24px;
`;

const PageTitle = styled.h1`
  font-size: ${theme.typography.h1};
  font-weight: 800;
  color: ${theme.colors.text};
  letter-spacing: -0.04em;
  margin: 0 0 6px 0;
`;

const PageSubtitle = styled.p`
  font-size: ${theme.typography.body};
  color: ${theme.colors.textMuted};
  margin: 0;
  line-height: 1.5;
`;

const DashboardGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(min(100%, 320px), 1fr));
  gap: 24px;
  width: 100%;
  align-items: stretch;
`;

const ActivePlanCard = styled.div`
  background: ${theme.colors.card};
  border: 1px solid ${theme.colors.cardBorder};
  border-radius: ${theme.radius.xl};
  padding: 28px 28px 24px;
  box-shadow: ${theme.shadows.card};
  position: relative;
  display: flex;
  flex-direction: column;
  min-height: 220px;
  transition: transform 0.25s ease, box-shadow 0.25s ease;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 3px;
    background: ${theme.colors.accentGradient};
  }

  &:hover {
    transform: translateY(-4px);
    box-shadow: ${theme.shadows.cardHover};
    border-color: rgba(255, 107, 107, 0.22);
  }
`;

const PlanTitle = styled.div`
  font-weight: 700;
  font-size: 1.125rem;
  color: ${theme.colors.text};
  margin-bottom: 16px;
  line-height: 1.35;
  letter-spacing: -0.02em;
  padding-right: 8px;
`;

const PlanPrice = styled.div`
  font-size: 2rem;
  font-weight: 800;
  color: ${theme.colors.primary};
  margin-bottom: 4px;
  letter-spacing: -0.03em;
  line-height: 1;
`;

const PlanSub = styled.div`
  color: ${theme.colors.textMuted};
  font-size: 14px;
  margin-bottom: auto;
`;

const TrialBadge = styled.div`
  margin-top: 16px;
  padding: 10px 14px;
  background: linear-gradient(135deg, #fff5f5 0%, #ffe4e4 100%);
  border: 1px solid rgba(255, 107, 107, 0.25);
  border-radius: ${theme.radius.md};
  font-size: 13px;
  color: ${theme.colors.primary};
  font-weight: 600;
  display: flex;
  align-items: center;
  gap: 8px;
`;

const LocationsBadge = styled.div`
  margin-top: 16px;
  padding: 12px 16px;
  background: #f8fafc;
  border: 1px solid ${theme.colors.cardBorder};
  border-radius: ${theme.radius.md};
  font-size: 14px;
  color: ${theme.colors.textSecondary};

  strong {
    color: ${theme.colors.text};
    font-weight: 600;
  }
`;

const NoPlanCard = styled.div`
  background: ${theme.colors.card};
  border: 2px dashed ${theme.colors.cardBorder};
  border-radius: ${theme.radius.xl};
  padding: 48px 32px;
  text-align: center;
  color: ${theme.colors.textMuted};
  font-size: 16px;
  box-shadow: ${theme.shadows.sm};
  grid-column: 1 / -1;
  max-width: 480px;
  margin: 0 auto;
  width: 100%;
`;

const LoadingState = styled.div`
  padding: 48px;
  text-align: center;
  color: ${theme.colors.textMuted};
  font-size: 15px;
`;

const Dashboard: React.FC = () => {
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [isMobileOpen, setIsMobileOpen] = useState(false);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [activePlans, setActivePlans] = useState<any[]>([]);
  const [loadingPlan, setLoadingPlan] = useState(true);
  const navigate = useNavigate();
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Get user data from localStorage that was saved during login
  const user = JSON.parse(localStorage.getItem('user') || '{}');
  const userName = user.name || '';
  const DEMO_EMAILS = ['zeel.prajapati.tot@gmail.com', 'dev.patel@theonetechnologies.co.in'];
  const isDemoUser =
    user.isDemoUser === true ||
    DEMO_EMAILS.includes(String(user.email || '').trim().toLowerCase());

  const {
    showLogoutConfirm,
    handleLogoutClick,
    handleLogoutConfirm,
    handleLogoutCancel
  } = useLogoutHandler();

  // Add click outside handler
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsDropdownOpen(false);
      }
    }

    // Attach the event listener
    document.addEventListener('mousedown', handleClickOutside);

    // Clean up the event listener
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [dropdownRef]);

  useEffect(() => {
    const fetchActivePlans = async () => {
      setLoadingPlan(true);
      try {
        const result = await getSubscribedLocations();
        const subs = result.subscriptions || [];
        const active = subs.filter(
          (s) =>
            isPaidSubscriptionStatus(s.status) &&
            (isDemoUser || !isTestSubscription(s))
        );

        // Add trial information to each plan
        const plansWithTrialInfo = active.map(plan => {
          const subscriptionCreatedDate = new Date(plan.subscriptionCreatedDate);
          const subscriptionEndDate = new Date(plan.subscriptionEndDate);

          // Calculate trial end date (14 days from creation)
          const trialEndDate = new Date(subscriptionCreatedDate);
          trialEndDate.setDate(trialEndDate.getDate() + 14);

          const now = new Date();
          const isTrialActive = now < trialEndDate && plan.status.toLowerCase() === 'authenticated';

          // Calculate due date based on trial status
          let dueDate: string;
          if (isTrialActive) {
            dueDate = trialEndDate.toLocaleDateString();
          } else {
            dueDate = subscriptionEndDate.toLocaleDateString();
          }

          return {
            ...plan,
            isTrialActive,
            trialEndDate: trialEndDate.toLocaleDateString(),
            actualDueDate: subscriptionEndDate.toLocaleDateString(),
            dueDate
          };
        });

        setActivePlans(plansWithTrialInfo);
      } catch {
        setActivePlans([]);
      } finally {
        setLoadingPlan(false);
      }
    };
    fetchActivePlans();
  }, []);

  const handleLogout = () => {
    handleLogoutClick();
  };

  const toggleMobileMenu = () => {
    setIsMobileOpen(!isMobileOpen);
  };

  return (
    <DashboardContainer>
      <Sidebar
        isCollapsed={isCollapsed}
        isMobileOpen={isMobileOpen}
        onMobileClose={toggleMobileMenu}
      />

      <MainContent isCollapsed={isCollapsed}>
        <TopBar isCollapsed={isCollapsed}>
          <ToggleButton onClick={() => {
            if (window.innerWidth <= 768) {
              toggleMobileMenu();
            } else {
              setIsCollapsed(!isCollapsed);
            }
          }}>
            <FontAwesomeIcon icon={faBars} />
          </ToggleButton>
          <UserProfile ref={dropdownRef} onClick={() => setIsDropdownOpen(!isDropdownOpen)}>
            <UserInfo>
              <UserName>{userName}</UserName>
            </UserInfo>
            <UserAvatar>{userName.charAt(0)}</UserAvatar>
            <UserDropdown isOpen={isDropdownOpen}>
              <DropdownItem onClick={() => navigate('/profile')}>
                <FontAwesomeIcon icon={faUser} />
                Profile
              </DropdownItem>
              <DropdownItem onClick={handleLogout}>
                <FontAwesomeIcon icon={faSignOutAlt} />
                Logout
              </DropdownItem>
            </UserDropdown>
          </UserProfile>
        </TopBar>

        <PageContent>
          <PageHeader>
            <PageTitle>Your Plans</PageTitle>
            <PageSubtitle>Overview of your active subscriptions</PageSubtitle>
          </PageHeader>

          {loadingPlan ? (
            <LoadingState>Loading your plans…</LoadingState>
          ) : activePlans.length > 0 ? (
            <DashboardGrid>
              {activePlans.map((plan, index) => (
                <ActivePlanCard key={plan._id || index}>
                  <PlanTitle>{plan.planName || 'Active Plan'}</PlanTitle>
                  <PlanPrice>{getStoredSubscriptionDisplayPrice(plan)}</PlanPrice>
                  <PlanSub>Per Sub-Account</PlanSub>

                  {plan.isTrialActive && (
                    <TrialBadge>
                      <span>⏰</span>
                      <span>Trial active — ends {plan.trialEndDate}</span>
                    </TrialBadge>
                  )}

                  {plan.subAccounts && plan.subAccounts.length > 0 && (
                    <LocationsBadge>
                      <strong>Connected Locations:</strong> {plan.subAccounts.length}
                    </LocationsBadge>
                  )}
                </ActivePlanCard>
              ))}
            </DashboardGrid>
          ) : (
            <DashboardGrid>
              <NoPlanCard>No active plans. Purchase a subscription to get started.</NoPlanCard>
            </DashboardGrid>
          )}
        </PageContent>

        <Footer isCollapsed={isCollapsed} />
      </MainContent>

      <LogoutConfirmDialog
        isOpen={showLogoutConfirm}
        onConfirm={handleLogoutConfirm}
        onCancel={handleLogoutCancel}
      />
    </DashboardContainer>
  );
};

export default Dashboard; 